import { useEffect, useState } from "react";
import DashboardShell from "@/components/dashboard/DashboardShell";
import {
  CoinOverviewCard,
  CoinPriceChart,
  CryptoStatus,
  TopCoinsTable,
  TopGainersTable,
  TopLosersTable,
} from "@/features/crypto/CryptoDashboardParts";
import {
  useCryptoHistory,
  useCryptoMarkets,
  useCryptoOverview,
} from "@/features/crypto/useCryptoMarket";
import type { CoinId, HistoryDays } from "@/features/crypto/types";

export default function CryptoDashboard() {
  const { data, loading, refreshing, error, refresh } = useCryptoOverview();
  const { data: markets } = useCryptoMarkets();
  const [coinId, setCoinId] = useState<CoinId>("bitcoin");
  const [days, setDays] = useState<HistoryDays>(7);
  const { data: history, loading: historyLoading } = useCryptoHistory(coinId, days);

  useEffect(() => {
    document.title = "Kryptowährungen · CashFUX";
  }, []);

  return (
    <DashboardShell
      title="Kryptowährungen"
      subtitle="Echte Marktdaten von CoinGecko"
    >
      <div className="market-dashboard text-white">
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold">Kryptomarkt</h1>
            <p className="mt-1 text-sm text-white/40">
              Kurse, Verlauf und Markttabellen
            </p>
          </div>
          {data && <CryptoStatus data={data} refreshing={refreshing} refresh={() => void refresh()} />}
        </div>
        {loading && (
          <div className="mt-8 grid animate-pulse gap-4 sm:grid-cols-2 xl:grid-cols-5">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-40 border border-white/10 bg-[#07080d]" />
            ))}
          </div>
        )}
        {error && (
          <div className="mt-8 border-l-4 border-[#e01e37] bg-[#07080d] p-5">
            <p className="font-semibold">Krypto-Kurse nicht verfügbar</p>
            <p className="mt-1 text-sm text-white/40">{error}</p>
            <button
              onClick={() => void refresh()}
              className="mt-4 h-10 bg-[#e01e37] px-4 text-sm font-semibold text-white"
            >
              Erneut versuchen
            </button>
          </div>
        )}
        {data && (
          <div className="mt-8 space-y-6">
            <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
              {data.items.map((coin) => (
                <CoinOverviewCard
                  key={coin.id}
                  coin={coin}
                  selected={coin.id === coinId}
                  onSelect={() => setCoinId(coin.id as CoinId)}
                />
              ))}
            </section>
            {!data.items.length && (
              <div className="border border-dashed border-white/10 bg-[#07080d] p-10 text-center text-white/40">
                Keine Krypto-Kurse verfügbar.
              </div>
            )}
            <CoinPriceChart history={history} loading={historyLoading} days={days} onDays={setDays} />
            {markets && (
              <div className="grid gap-6 lg:grid-cols-3">
                <TopCoinsTable rows={markets.topCoins} />
                <TopGainersTable rows={markets.topGainers} />
                <TopLosersTable rows={markets.topLosers} />
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
